import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { merchantApi } from '../../api/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar, faFire, faArrowTrendUp, faPercent, faArrowUp, faArrowDown, faMinus,
  faCoins, faMoneyBillWave, faClipboardList, faRotate, faClock,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { UiPageHeader, UiStatsGrid } from '../../components/ui';
import type { StatCardDef } from '../../components/ui';

interface HourlyData {
  hour: number;
  total_orders: number;
  paid_orders: number;
  paid_amount: number;
}

interface DailyData {
  date: string;
  total_orders: number;
  paid_orders: number;
  paid_amount: number;
  fee_amount: number;
  expired_orders: number;
  cancelled_orders: number;
}

interface AnalyticsData {
  hourly: HourlyData[];
  daily: DailyData[];
  peak_hour: { hour: number; paid_orders: number } | null;
  avg_order_value: number;
}

interface PayoutBucket {
  label: string;
  count: number;
  total_amount: number;
}

interface PayoutDistData {
  distribution: PayoutBucket[];
  total_payouts: number;
  total_amount: number;
}

const CARD_CLASS = 'bg-white dark:bg-slate-800 p-4 rounded-xl shadow-theme';
const CARD_TITLE_CLASS = 'text-sm font-bold text-gray-700 dark:text-slate-200 mb-3 pb-2 border-b border-gray-100 dark:border-slate-700 flex items-center gap-2';

const Skeleton = ({ className = '', style }: { className?: string; style?: React.CSSProperties }) => (
  <div
    className={`animate-pulse bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-slate-700 dark:via-slate-600 dark:to-slate-700 bg-[length:200%_100%] rounded ${className}`}
    style={{ animation: 'shimmer 1.5s infinite', backgroundSize: '200% 100%', ...style }}
  />
);

const SkeletonRow = () => (
  <div className="flex justify-between items-center py-2.5 px-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
    <Skeleton className="h-4 w-28" />
    <Skeleton className="h-4 w-20" />
  </div>
);

const SkeletonCard = ({ rows = 4, title }: { rows?: number; title?: string }) => (
  <div className={CARD_CLASS}>
    {title ? (
      <h2 className={CARD_TITLE_CLASS}>{title}</h2>
    ) : (
      <Skeleton className="h-5 w-32 mb-3" />
    )}
    <div className="flex flex-col gap-2">
      {Array.from({ length: rows }).map((_, i) => <SkeletonRow key={i} />)}
    </div>
  </div>
);

const StatRow = ({ label, value, valueClass = 'text-gray-800 dark:text-slate-100' }: {
  label: string;
  value: string | number;
  valueClass?: string;
}) => (
  <div className="flex justify-between items-center py-2.5 px-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg border-l-3 border-gray-200 dark:border-slate-600 transition-all duration-200 hover:bg-orange-50 dark:hover:bg-orange-900/20 hover:border-orange-500">
    <span className="text-gray-500 dark:text-slate-400 font-medium text-sm">{label}</span>
    <strong className={`text-base font-bold ${valueClass}`}>{value}</strong>
  </div>
);

const ChangeIndicator = ({ value, percent, suffix = '' }: { value: number; percent: number; suffix?: string }) => {
  const isUp = value > 0;
  const isZero = value === 0;
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold
      ${isZero ? 'text-gray-400 dark:text-slate-500' : isUp ? 'text-green-600 dark:text-green-400' : 'text-red-500 dark:text-red-400'}`}>
      <FontAwesomeIcon icon={isZero ? faMinus : isUp ? faArrowUp : faArrowDown} className="text-[10px]" />
      {isZero ? '-' : `${isUp ? '+' : ''}${percent.toFixed(1)}%`}
      {!isZero && suffix && (
        <span className="text-gray-400 dark:text-slate-500 font-normal ml-0.5">
          ({isUp ? '+' : ''}{typeof value === 'number' && Math.abs(value) > 999 ? value.toLocaleString() : value}{suffix})
        </span>
      )}
    </span>
  );
};

const getTodayVsYesterday = (analytics: AnalyticsData | null) => {
  if (!analytics?.daily || analytics.daily.length < 2) return null;
  const today = analytics.daily[analytics.daily.length - 1];
  const yesterday = analytics.daily[analytics.daily.length - 2];
  if (!today || !yesterday) return null;

  const ordersDiff = today.total_orders - yesterday.total_orders;
  const ordersPercent = yesterday.total_orders > 0 ? ((ordersDiff / yesterday.total_orders) * 100) : 0;
  const paidDiff = today.paid_orders - yesterday.paid_orders;
  const paidPercent = yesterday.paid_orders > 0 ? ((paidDiff / yesterday.paid_orders) * 100) : 0;
  const revDiff = (today.paid_amount || 0) - (yesterday.paid_amount || 0);
  const revPercent = (yesterday.paid_amount || 0) > 0 ? ((revDiff / yesterday.paid_amount) * 100) : 0;

  return { today, yesterday, ordersDiff, ordersPercent, paidDiff, paidPercent, revDiff, revPercent };
};

const AdminAnalyticsPage: React.FC = () => {
  const [analytics, setAnalytics] = useState<AnalyticsData | null>(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [payoutDist, setPayoutDist] = useState<PayoutDistData | null>(null);
  const [payoutDistLoading, setPayoutDistLoading] = useState(true);

  const loadAnalytics = useCallback(async () => {
    try {
      const res = await merchantApi.get('/admin/orders/analytics');
      setAnalytics(res.data.data);
    } catch {
      // silent fail for analytics
    } finally {
      setAnalyticsLoading(false);
    }
  }, []);

  const loadPayoutDistribution = useCallback(async () => {
    try {
      const res = await merchantApi.get('/admin/payouts/distribution');
      setPayoutDist(res.data.data);
    } catch {
      // silent
    } finally {
      setPayoutDistLoading(false);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    setAnalyticsLoading(true);
    setPayoutDistLoading(true);
    await Promise.all([loadAnalytics(), loadPayoutDistribution()]);
  }, [loadAnalytics, loadPayoutDistribution]);

  useEffect(() => {
    refreshAll();
    const interval = setInterval(refreshAll, 300000);
    return () => clearInterval(interval);
  }, [refreshAll]);

  const comparison = getTodayVsYesterday(analytics);

  const todayPaidOrders = useMemo(
    () => (analytics?.hourly || []).reduce((s, h) => s + (h.paid_orders || 0), 0),
    [analytics],
  );

  const todayPaidAmount = useMemo(
    () => (analytics?.hourly || []).reduce((s, h) => s + Number(h.paid_amount || 0), 0),
    [analytics],
  );

  const statCards: StatCardDef[] = useMemo(() => {
    if (!analytics) return [];
    return [
      {
        icon: faClock,
        iconClassName: 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400',
        label: 'Peak Hour',
        value: (
          <span className="text-orange-600 dark:text-orange-400">
            {analytics.peak_hour
              ? `${String(analytics.peak_hour.hour).padStart(2, '0')}:00`
              : '-'}
          </span>
        ),
        subValue: (
          <span className="text-xs text-gray-400 dark:text-slate-500">
            {analytics.peak_hour ? `${analytics.peak_hour.paid_orders} orders` : 'ไม่มีข้อมูล'}
          </span>
        ),
      },
      {
        icon: faCoins,
        iconClassName: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
        label: 'ยอดเฉลี่ยต่อ Order',
        value: (
          <span className="text-purple-600 dark:text-purple-400">
            ฿{Number(analytics.avg_order_value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}
          </span>
        ),
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">วันนี้</span>,
      },
      {
        icon: faFire,
        iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400',
        label: 'Orders วันนี้ (Paid)',
        value: <span className="text-green-600 dark:text-green-400">{todayPaidOrders.toLocaleString()}</span>,
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">รายการสำเร็จ</span>,
      },
      {
        icon: faMoneyBillWave,
        iconClassName: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
        label: 'ยอดรวมวันนี้ (Paid)',
        value: (
          <span className="text-blue-600 dark:text-blue-400">
            ฿{todayPaidAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}
          </span>
        ),
        subValue: <span className="text-xs text-gray-400 dark:text-slate-500">รายได้วันนี้</span>,
      },
    ];
  }, [analytics, todayPaidOrders, todayPaidAmount]);

  const trendColumns: Column<DailyData>[] = useMemo(() => [
    {
      key: 'date',
      header: 'วันที่',
      render: (d) => (
        <span className="text-gray-600 dark:text-slate-300 font-medium">
          {new Date(d.date + 'T00:00:00').toLocaleDateString('th-TH', { weekday: 'short', day: 'numeric', month: 'short' })}
        </span>
      ),
    },
    {
      key: 'total_orders',
      header: 'Orders',
      sortable: true,
      render: (d) => <span className="text-gray-700 dark:text-slate-200 font-medium">{d.total_orders}</span>,
    },
    {
      key: 'paid_orders',
      header: 'Paid',
      sortable: true,
      render: (d) => <span className="text-green-600 dark:text-green-400 font-bold">{d.paid_orders}</span>,
    },
    {
      key: 'paid_amount',
      header: 'ยอด',
      sortable: true,
      render: (d) => (
        <span className="text-blue-600 dark:text-blue-400 font-medium">
          ฿{Number(d.paid_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 0 })}
        </span>
      ),
    },
    {
      key: 'success_rate',
      header: 'Rate',
      sortable: true,
      render: (d) => {
        const rate = d.total_orders > 0 ? ((d.paid_orders / d.total_orders) * 100) : 0;
        return (
          <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold
            ${rate >= 70 ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
              : rate >= 40 ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400'
              : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'}`}>
            <FontAwesomeIcon icon={faPercent} className="text-[8px]" />
            {rate.toFixed(0)}
          </span>
        );
      },
    },
  ], []);

  const loading = analyticsLoading || payoutDistLoading;

  return (
    <div className="max-w-full">
      <style>{`@keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`}</style>

      <UiPageHeader
        icon={faChartBar}
        title="Order Analytics"
        subtitle="สรุป Orders และ Revenue แบบ real-time — Auto-refresh ทุก 5 นาที"
        actions={
          <button
            type="button"
            onClick={() => refreshAll()}
            disabled={loading}
            className="px-4 py-2 rounded-lg text-sm font-medium bg-white/20 hover:bg-white/30 disabled:opacity-50 text-white flex items-center gap-2 backdrop-blur-sm border border-white/20"
          >
            <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} />
            Refresh
          </button>
        }
      />

      {!analyticsLoading && analytics && (
        <UiStatsGrid cards={statCards} loading={analyticsLoading} cols={4} className="mb-6" />
      )}

      {/* Order Analytics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        {analyticsLoading ? (
          <>
            <div className={`${CARD_CLASS} lg:col-span-2`}>
              <Skeleton className="h-5 w-48 mb-4" />
              <div className="flex items-end gap-[3px] h-40 px-2">
                {Array.from({ length: 24 }).map((_, i) => (
                  <div key={i} className="flex-1 flex flex-col items-end justify-end h-full">
                    <Skeleton className="w-full rounded-t" style={{ height: `${30 + (i % 5) * 10}%` }} />
                  </div>
                ))}
              </div>
            </div>
            <SkeletonCard rows={4} />
            <SkeletonCard rows={4} />
            <SkeletonCard rows={3} />
            <SkeletonCard rows={4} />
          </>
        ) : analytics && (
          <>
            {/* Hourly Chart - Today */}
            <div className={`${CARD_CLASS} lg:col-span-2`}>
              <h2 className={CARD_TITLE_CLASS}>
                <FontAwesomeIcon icon={faChartBar} className="text-blue-500 text-xs" />
                Orders รายชั่วโมง (วันนี้)
              </h2>
              <div className="flex items-end gap-[3px] h-40 px-2">
                {(analytics.hourly || []).map((h) => {
                  const maxOrders = Math.max(...(analytics.hourly || []).map((x) => x.total_orders || 0), 1);
                  const height = h.total_orders > 0 ? Math.max((h.total_orders / maxOrders) * 100, 4) : 0;
                  const isPeak = analytics.peak_hour && h.hour === analytics.peak_hour.hour && h.total_orders > 0;
                  return (
                    <div key={h.hour} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                      <div className="absolute bottom-full mb-1 hidden group-hover:block z-10">
                        <div className="bg-gray-800 dark:bg-slate-900 text-white text-[10px] rounded-lg px-2 py-1.5 whitespace-nowrap shadow-lg border border-slate-700">
                          <div className="font-bold">{String(h.hour).padStart(2, '0')}:00</div>
                          <div>ทั้งหมด: {h.total_orders}</div>
                          <div className="text-green-300">สำเร็จ: {h.paid_orders}</div>
                          <div className="text-yellow-300">฿{Number(h.paid_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</div>
                        </div>
                      </div>
                      <div
                        className={`w-full rounded-t transition-all duration-300 cursor-pointer
                          ${isPeak ? 'bg-gradient-to-t from-orange-500 to-orange-300 ring-2 ring-orange-400 ring-offset-1 dark:ring-offset-slate-800'
                            : h.paid_orders > 0 ? 'bg-gradient-to-t from-blue-500 to-blue-300 hover:from-blue-600 hover:to-blue-400'
                            : h.total_orders > 0 ? 'bg-gradient-to-t from-gray-300 to-gray-200 dark:from-slate-500 dark:to-slate-400'
                            : 'bg-gray-100 dark:bg-slate-700'}`}
                        style={{ height: `${height}%`, minHeight: h.total_orders > 0 ? '4px' : '0px' }}
                      ></div>
                      <span className={`text-[9px] mt-1 ${isPeak ? 'text-orange-600 dark:text-orange-400 font-bold' : 'text-gray-400 dark:text-slate-500'}`}>
                        {h.hour % 3 === 0 ? `${String(h.hour).padStart(2, '0')}` : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="flex items-center gap-4 mt-3 text-[10px] text-gray-400 dark:text-slate-500 px-2">
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-t from-blue-500 to-blue-300 inline-block"></span> มี Paid
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-t from-gray-300 to-gray-200 dark:from-slate-500 dark:to-slate-400 inline-block"></span> ไม่มี Paid
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-gradient-to-t from-orange-500 to-orange-300 inline-block ring-1 ring-orange-400"></span> Peak Hour
                </span>
              </div>
            </div>

            {/* Key Metrics detail */}
            <div className={CARD_CLASS}>
              <h2 className={CARD_TITLE_CLASS}>
                <FontAwesomeIcon icon={faFire} className="text-red-500 text-xs" />
                Key Metrics วันนี้
              </h2>
              <div className="flex flex-col gap-2">
                <StatRow
                  label="Peak Hour:"
                  value={analytics.peak_hour ? `${String(analytics.peak_hour.hour).padStart(2, '0')}:00 (${analytics.peak_hour.paid_orders} orders)` : '-'}
                  valueClass="text-orange-600 dark:text-orange-400"
                />
                <StatRow
                  label="ยอดเฉลี่ยต่อ Order:"
                  value={`฿${Number(analytics.avg_order_value || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                  valueClass="text-purple-600 dark:text-purple-400"
                />
                <StatRow
                  label="Orders วันนี้ (Paid):"
                  value={`${todayPaidOrders} orders`}
                  valueClass="text-green-600 dark:text-green-400"
                />
                <StatRow
                  label="ยอดรวมวันนี้ (Paid):"
                  value={`฿${todayPaidAmount.toLocaleString('th-TH', { minimumFractionDigits: 2 })}`}
                  valueClass="text-blue-600 dark:text-blue-400"
                />
              </div>
            </div>

            {/* Today vs Yesterday */}
            {comparison && (
              <div className={CARD_CLASS}>
                <h2 className={CARD_TITLE_CLASS}>
                  <FontAwesomeIcon icon={faArrowTrendUp} className="text-indigo-500 text-xs" />
                  วันนี้ vs เมื่อวาน
                </h2>
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between items-center py-2.5 px-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                    <div>
                      <span className="text-gray-500 dark:text-slate-400 font-medium text-sm">Orders</span>
                      <div className="text-xs text-gray-400 dark:text-slate-500">{comparison.today.total_orders} vs {comparison.yesterday.total_orders}</div>
                    </div>
                    <ChangeIndicator value={comparison.ordersDiff} percent={comparison.ordersPercent} />
                  </div>
                  <div className="flex justify-between items-center py-2.5 px-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                    <div>
                      <span className="text-gray-500 dark:text-slate-400 font-medium text-sm">Paid Orders</span>
                      <div className="text-xs text-gray-400 dark:text-slate-500">{comparison.today.paid_orders} vs {comparison.yesterday.paid_orders}</div>
                    </div>
                    <ChangeIndicator value={comparison.paidDiff} percent={comparison.paidPercent} />
                  </div>
                  <div className="flex justify-between items-center py-2.5 px-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                    <div>
                      <span className="text-gray-500 dark:text-slate-400 font-medium text-sm">Revenue</span>
                      <div className="text-xs text-gray-400 dark:text-slate-500">
                        ฿{Number(comparison.today.paid_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 0 })} vs ฿{Number(comparison.yesterday.paid_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 0 })}
                      </div>
                    </div>
                    <ChangeIndicator value={comparison.revDiff} percent={comparison.revPercent} />
                  </div>
                </div>
              </div>
            )}

            {/* 7-Day Revenue Chart */}
            <div className={CARD_CLASS}>
              <h2 className={CARD_TITLE_CLASS}>
                <FontAwesomeIcon icon={faCoins} className="text-yellow-500 text-xs" />
                Revenue 7 วัน
              </h2>
              {(() => {
                const daily = analytics.daily || [];
                const maxRev = Math.max(...daily.map((d) => d.paid_amount || 0), 1);
                const totalWeekRev = daily.reduce((s, d) => s + (d.paid_amount || 0), 0);
                const totalWeekFee = daily.reduce((s, d) => s + (d.fee_amount || 0), 0);
                return (
                  <div>
                    <div className="flex items-end gap-2 h-28 px-1 mb-2">
                      {daily.map((d) => {
                        const barHeight = (d.paid_amount || 0) > 0 ? Math.max(((d.paid_amount || 0) / maxRev) * 100, 4) : 0;
                        return (
                          <div key={d.date} className="flex-1 flex flex-col items-center justify-end h-full group relative">
                            <div className="absolute bottom-full mb-1 hidden group-hover:block z-10">
                              <div className="bg-gray-800 dark:bg-slate-900 text-white text-[10px] rounded-lg px-2 py-1 whitespace-nowrap shadow-lg border border-slate-700">
                                <div>฿{Number(d.paid_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 0 })}</div>
                                <div className="text-red-300">Fee: ฿{Number(d.fee_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 0 })}</div>
                              </div>
                            </div>
                            <div
                              className="w-full rounded-t bg-gradient-to-t from-green-500 to-emerald-300 hover:from-green-600 hover:to-emerald-400 transition-all duration-300 cursor-pointer"
                              style={{ height: `${barHeight}%`, minHeight: (d.paid_amount || 0) > 0 ? '4px' : '0px' }}
                            ></div>
                            <span className="text-[9px] mt-1 text-gray-400 dark:text-slate-500">
                              {new Date(d.date + 'T00:00:00').toLocaleDateString('th-TH', { weekday: 'short' })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex justify-between text-[10px] text-gray-400 dark:text-slate-500 px-1 border-t border-gray-100 dark:border-slate-700 pt-2">
                      <span>รวม 7 วัน: <strong className="text-green-600 dark:text-green-400">฿{totalWeekRev.toLocaleString('th-TH', { minimumFractionDigits: 0 })}</strong></span>
                      <span>Fee: <strong className="text-red-500 dark:text-red-400">฿{totalWeekFee.toLocaleString('th-TH', { minimumFractionDigits: 0 })}</strong></span>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Order Status Distribution */}
            <div className={CARD_CLASS}>
              <h2 className={CARD_TITLE_CLASS}>
                <FontAwesomeIcon icon={faClipboardList} className="text-blue-500 text-xs" />
                สัดส่วน Order Status (7 วัน)
              </h2>
              {(() => {
                const daily = analytics.daily || [];
                const totalOrders = daily.reduce((s, d) => s + (d.total_orders || 0), 0);
                const paidOrders = daily.reduce((s, d) => s + (d.paid_orders || 0), 0);
                const expiredOrders = daily.reduce((s, d) => s + (d.expired_orders || 0), 0);
                const cancelledOrders = daily.reduce((s, d) => s + (d.cancelled_orders || 0), 0);
                const pendingOrders = totalOrders - paidOrders - expiredOrders - cancelledOrders;
                const paidPct = totalOrders > 0 ? (paidOrders / totalOrders) * 100 : 0;
                const expiredPct = totalOrders > 0 ? (expiredOrders / totalOrders) * 100 : 0;
                const cancelledPct = totalOrders > 0 ? (cancelledOrders / totalOrders) * 100 : 0;
                const pendingPct = totalOrders > 0 ? (pendingOrders / totalOrders) * 100 : 0;
                return (
                  <div>
                    <div className="h-6 rounded-full overflow-hidden flex bg-gray-100 dark:bg-slate-700 mb-3">
                      {paidPct > 0 && <div className="bg-green-500 transition-all duration-500" style={{ width: `${paidPct}%` }}></div>}
                      {pendingPct > 0 && <div className="bg-yellow-400 transition-all duration-500" style={{ width: `${pendingPct}%` }}></div>}
                      {expiredPct > 0 && <div className="bg-red-400 transition-all duration-500" style={{ width: `${expiredPct}%` }}></div>}
                      {cancelledPct > 0 && <div className="bg-gray-400 dark:bg-slate-500 transition-all duration-500" style={{ width: `${cancelledPct}%` }}></div>}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="flex items-center gap-2 px-2 py-1.5 bg-green-50 dark:bg-green-900/20 rounded-lg">
                        <span className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0"></span>
                        <span className="text-gray-600 dark:text-slate-300">Paid</span>
                        <span className="ml-auto font-bold text-green-600 dark:text-green-400">{paidOrders.toLocaleString()} ({paidPct.toFixed(1)}%)</span>
                      </div>
                      <div className="flex items-center gap-2 px-2 py-1.5 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                        <span className="w-3 h-3 rounded-full bg-yellow-400 flex-shrink-0"></span>
                        <span className="text-gray-600 dark:text-slate-300">Pending</span>
                        <span className="ml-auto font-bold text-yellow-600 dark:text-yellow-400">{pendingOrders.toLocaleString()} ({pendingPct.toFixed(1)}%)</span>
                      </div>
                      <div className="flex items-center gap-2 px-2 py-1.5 bg-red-50 dark:bg-red-900/20 rounded-lg">
                        <span className="w-3 h-3 rounded-full bg-red-400 flex-shrink-0"></span>
                        <span className="text-gray-600 dark:text-slate-300">Expired</span>
                        <span className="ml-auto font-bold text-red-500 dark:text-red-400">{expiredOrders.toLocaleString()} ({expiredPct.toFixed(1)}%)</span>
                      </div>
                      <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                        <span className="w-3 h-3 rounded-full bg-gray-400 dark:bg-slate-500 flex-shrink-0"></span>
                        <span className="text-gray-600 dark:text-slate-300">Cancelled</span>
                        <span className="ml-auto font-bold text-gray-500 dark:text-slate-400">{cancelledOrders.toLocaleString()} ({cancelledPct.toFixed(1)}%)</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
          </>
        )}
      </div>

      {/* 7-Day Trend Table */}
      {!analyticsLoading && analytics && (
        <DataTable
          data={analytics.daily || []}
          columns={trendColumns}
          loading={false}
          keyExtractor={(row) => row.date}
          emptyMessage="ไม่มีข้อมูลเทรนด์"
          pagination={{ pageSize: 7, showPageSizeSelector: false }}
          headerContent={
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faArrowTrendUp} className="text-green-500" />
              <h2 className="text-lg font-bold text-gray-800 dark:text-slate-100">เทรนด์ 7 วัน</h2>
            </div>
          }
          hoverable
          className="mb-6"
        />
      )}

      {/* Payout Distribution */}
      <div className="grid grid-cols-1 gap-4 mb-6">
        {payoutDistLoading ? (
          <SkeletonCard rows={5} title="การถอนตามช่วงจำนวนเงิน (7 วัน)" />
        ) : payoutDist && (
          <div className={CARD_CLASS}>
            <h2 className={CARD_TITLE_CLASS}>
              <FontAwesomeIcon icon={faMoneyBillWave} className="text-emerald-500 text-xs" />
              การถอนตามช่วงจำนวนเงิน (7 วัน)
            </h2>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
              {(payoutDist.distribution || []).map((d) => {
                const maxCount = Math.max(...(payoutDist.distribution || []).map((x) => x.count || 0), 1);
                const barPct = d.count > 0 ? (d.count / maxCount) * 100 : 0;
                const totalPct = payoutDist.total_payouts > 0 ? ((d.count / payoutDist.total_payouts) * 100) : 0;
                return (
                  <div
                    key={d.label}
                    className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-3 border border-gray-100 dark:border-slate-600 hover:border-emerald-200 dark:hover:border-emerald-700 hover:bg-emerald-50/30 dark:hover:bg-emerald-900/20 transition-all duration-200"
                  >
                    <div className="text-xs text-gray-400 dark:text-slate-500 font-medium mb-1">฿{d.label}</div>
                    <div className="text-2xl font-bold text-gray-800 dark:text-slate-100">{d.count.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-400 dark:text-slate-500 mb-2">รายการ ({totalPct.toFixed(1)}%)</div>
                    <div className="h-1.5 rounded-full bg-gray-200 dark:bg-slate-600 overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all duration-500"
                        style={{ width: `${barPct}%` }}
                      ></div>
                    </div>
                    <div className="text-[10px] text-gray-400 dark:text-slate-500 mt-1.5">
                      ยอดรวม: <strong className="text-emerald-600 dark:text-emerald-400">฿{Number(d.total_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 0 })}</strong>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between text-xs text-gray-400 dark:text-slate-500 px-1 pt-2 border-t border-gray-100 dark:border-slate-700">
              <span>รวมทั้งหมด: <strong className="text-gray-700 dark:text-slate-200">{(payoutDist.total_payouts || 0).toLocaleString()} รายการ</strong></span>
              <span>ยอดรวม: <strong className="text-emerald-600 dark:text-emerald-400">฿{Number(payoutDist.total_amount || 0).toLocaleString('th-TH', { minimumFractionDigits: 2 })}</strong></span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAnalyticsPage;
